"use client";

import clsx from "clsx";
import { useState } from "react";

import type { BrowseRoomTypeWithAvailability } from "../../_hooks/use-room-type-search";
import {
  hasUnavailableRoomTypeForRange,
  selectDisplayedRoomTypes,
} from "../../_utils/room-type-availability-view";
import { ChevronDownIcon } from "../../ui/i/chevron-down";
import { PillButton } from "../../ui/pill-button";

import { ArchSpecRow } from "./_components/arch-spec-row";
import { RoomTypeRow } from "./room-type-row";

/**
 * 建築タイプ単位のグループ。常時展開表示する（旧・折りたたみトグルは Figma に対応 UI が
 * 無い一時実装だったため撤去済み）。
 * 満室部屋タイプは日程フィルタ指定時のみ初期非表示にし、「全ての部屋を見る」で表示に切り替える
 * （片方向。一度表示したら再度隠す操作は無い）。
 */
export function ArchitectureTypeGroup({
  roomTypes,
  firstItem = false,
  showBorder = true,
}: {
  roomTypes: BrowseRoomTypeWithAvailability[];
  /** 先頭グループの SP border-t は隣接要素との区切りにならず浮くため外す */
  firstItem?: boolean;
  /** モーダル表示幅では lg:border がビューポート基準で誤発火するため呼び出し元から無効化する */
  showBorder?: boolean;
}) {
  const [showUnavailable, setShowUnavailable] = useState(false);
  const arch = roomTypes[0]?.architectureType ?? null;
  const displayedRoomTypes = selectDisplayedRoomTypes(
    roomTypes,
    showUnavailable,
  );

  return (
    <div
      className={clsx(
        "border-dx-border-default flex flex-col gap-[8px] py-[16px]",
        showBorder && "lg:border",
        !firstItem && "border-t",
      )}
    >
      <div className="flex flex-col gap-[8px] px-[16px]">
        <p className="font-dx text-dx-text-default min-w-px flex-1 text-[22px] leading-[28px] font-medium">
          {arch?.name ?? "その他"}
        </p>
      </div>

      <ArchSpecRow arch={arch} className="px-[16px] pb-[12px]" />
      <div className="flex flex-col">
        {displayedRoomTypes.map((roomType) => (
          <RoomTypeRow key={roomType.id} roomType={roomType} />
        ))}
      </div>
      {!showUnavailable && hasUnavailableRoomTypeForRange(roomTypes) && (
        <div className="flex justify-center px-[16px]">
          <PillButton
            variant="ghost"
            size="small"
            borderTone="muted"
            trailingIcon={<ChevronDownIcon className="size-[16px]" />}
            data-testid="show-unavailable-toggle"
            onClick={() => setShowUnavailable(true)}
          >
            全ての部屋を見る
          </PillButton>
        </div>
      )}
    </div>
  );
}
